import { useState, useCallback, useMemo, ChangeEvent, KeyboardEvent, ClipboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import fplToken from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { styled } from '@/UI/theme.exports';
import { Box } from '@/UI/Box/Box';
import { Chip } from '@/UI/Chip/Chip';
import { Input } from '@/UI/Input/Input';
import { EmbedInput } from '@/UI/EmbedInput/EmbedInput';
import { Typography } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { emailRegex } from '@/utils/strings.utils';
import { AppPaper, Flex, FlexCenVer } from '@/UI/styles';
import { WorkspaceRole } from '@/types/auth.types';
import { XIcon } from '@/UI/Icons/XIcon';
import { RoleSelect } from '@/components/Settings/Team/TeamTable/RoleSelect';

const StyledPaper = styled(AppPaper, {
  // oxlint-disable-next-line typescript/no-unnecessary-type-assertion -- MUI types this prop as PropertyKey.
  shouldForwardProp: (prop) => !['isFilled', 'isFocused', 'hasError', 'isFpl'].includes(prop as string),
})<{ isFilled: boolean; isFocused: boolean; hasError: boolean; isFpl?: boolean }>(
  ({ theme, isFilled, isFocused, hasError, isFpl }) => ({
    position: 'relative',
    boxSizing: 'border-box',
    padding: isFpl ? theme.spacing(1) : `${isFilled ? theme.spacing(1) : 0} ${theme.spacing(1)}`,
    minHeight: isFpl ? 32 : undefined,
    borderRadius: '4px',
    ...(isFpl
      ? {
          // Adopt the Figma input look: no border, subtle grey fill. Focus and
          // error are shown via an inset outline so the layout never shifts.
          border: 'none',
          backgroundColor: fplToken.color.bgSecondary,
          outline: hasError
            ? `1px solid ${fplToken.color.borderDangerStrong}`
            : isFocused
              ? `1px solid ${fplToken.color.borderSelected}`
              : 'none',
          outlineOffset: '-1px',
        }
      : {
          border: `1px solid ${hasError ? color.Weavy_Error_64_T : isFocused ? color.White40_T : token.border}`,
          ...(hasError && { boxShadow: `0 0 0 4px ${color.Weavy_Error_08_T}` }),
        }),
  }),
);

interface EmailChipsInputProps {
  emails: string[];
  currentRole?: WorkspaceRole;
  outsideErr?: string;
  inputW?: string;
  /** Empty-state prompt. Defaults to the shared "example@email.com" hint. */
  placeholder?: string;
  errorEmails?: string[];
  onSetEmails: (emails: string[]) => void;
  onInputChange: (value: string, isValid: boolean) => void;
  onSetRole?: (role: WorkspaceRole) => void;
}

function EmailChipsInput({
  emails,
  currentRole,
  inputW = '400px',
  errorEmails = [],
  placeholder,
  onSetEmails,
  onInputChange,
  onSetRole,
}: EmailChipsInputProps) {
  const { t } = useTranslation();
  const isFplEnabled = useFplMigrationFlag();
  const [inputValue, setInputValue] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);

  const isShowRoleSelection = !!currentRole;
  const emptyPlaceholder = placeholder ?? t(I18N_KEYS.SHARE_WORKFLOW_MODAL.EMAIL_PLACEHOLDER);
  const normalizedErrorEmails = useMemo(() => new Set(errorEmails.map((email) => email.toLowerCase())), [errorEmails]);

  const handleValueChange = (value: string) => {
    setInputValue(value);
    setError('');
    setSelectedEmail(null);

    // Notify parent of input changes and validity
    if (onInputChange) {
      onInputChange(value, value.trim() ? emailRegex.test(value.trim()) : false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => handleValueChange(e.target.value);

  const validateAndAddEmail = (email: string) => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      return;
    }

    if (!emailRegex.test(trimmedEmail)) {
      // setError(t(I18N_KEYS.SHARE_WORKFLOW_MODAL.INVALID_EMAIL));
      return;
    }

    if (emails.includes(trimmedEmail)) {
      const errMsg = `${trimmedEmail} has already been added`;
      setError(errMsg);
      return;
    }

    onSetEmails([...emails, trimmedEmail]);
    setInputValue('');
    setError('');
    setSelectedEmail(null);

    // Notify parent of empty input after adding email
    if (onInputChange) {
      onInputChange('', false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      return validateAndAddEmail(inputValue);
    }

    // Handle backspace when input is empty
    if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      e.preventDefault();
      const lastEmail = emails[emails.length - 1];

      if (selectedEmail === lastEmail) {
        // Second backspace - delete the email
        const newEmails = emails.filter((email) => email !== lastEmail);
        onSetEmails(newEmails);
        setSelectedEmail(null);
      } else {
        // First backspace - select the email
        setSelectedEmail(lastEmail);
      }
    }
  };

  const handleDoubleClick = (email: string) => {
    if (inputValue.trim()) {
      return;
    }

    setInputValue(email);
    const newEmails = emails.filter((e) => e !== email);
    onSetEmails(newEmails);
    setSelectedEmail(null);

    // Notify parent of input change
    if (onInputChange) {
      onInputChange(email, emailRegex.test(email));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const pastedEmails = pastedText.split(/[,;\s]+/).filter(Boolean);

    const currentEmails = [...emails];
    const newValidEmails: string[] = [];

    for (const raw of pastedEmails) {
      const trimmed = raw.trim();
      if (!trimmed || !emailRegex.test(trimmed)) continue;
      if (currentEmails.includes(trimmed) || newValidEmails.includes(trimmed)) continue;
      newValidEmails.push(trimmed);
    }

    if (newValidEmails.length > 0) {
      const updatedEmails = [...currentEmails, ...newValidEmails];
      onSetEmails(updatedEmails);
      setInputValue('');
      setError('');
      onInputChange('', false);
    }
    setSelectedEmail(null);
  };

  const handleDelete = useCallback(
    (emailToDelete) => {
      const newEmails = emails.filter((email) => email !== emailToDelete);
      onSetEmails(newEmails);
      setSelectedEmail(null);
    },
    [emails, onSetEmails],
  );

  // Clear selection when clicking outside
  const handleContainerClick = () => setSelectedEmail(null);

  return (
    <Box onClick={handleContainerClick} sx={{ flex: 1 }}>
      <StyledPaper
        data-testid="email-chips-field"
        sx={{ width: '100%', display: 'flex', alignItems: 'flex-start' }}
        isFpl={isFplEnabled}
        isFilled={emails.length > 0}
        isFocused={isInputFocused}
        hasError={errorEmails.length > 0}
        onFocus={() => setIsInputFocused(true)}
        onBlur={() => setIsInputFocused(false)}
      >
        <Flex sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap', alignItems: 'center', gap: 1, width: inputW }}>
          {emails.map((email) => {
            const isError = normalizedErrorEmails.has(email.toLowerCase());
            return (
              <Chip
                key={email}
                fplProps={{
                  selected: selectedEmail === email,
                }}
                label={email}
                onDelete={() => handleDelete(email)}
                onDoubleClick={() => handleDoubleClick(email)}
                variant="outlined"
                size="small"
                deleteIcon={<XIcon width={16} height={16} />}
                sx={{
                  transition: 'all 0.3s ease',
                  borderColor: token.border,
                  fontSize: '12px',
                  fontWeight: 400,
                  ...(isError && {
                    color: isFplEnabled ? fplToken.color.textDanger : color.Weavy_Error,
                    '& .MuiChip-deleteIcon': { color: token.textDanger },
                  }),
                  ...(selectedEmail === email && {
                    backgroundColor: color.White16_T,
                    borderColor: color.White40_T,
                    color: 'error.contrastText',
                    '& .MuiChip-deleteIcon': {
                      color: 'error.contrastText',
                    },
                  }),
                }}
              />
            );
          })}
          {isFplEnabled ? (
            <EmbedInput
              aria-label={emptyPlaceholder}
              layout="inline"
              value={inputValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={emails.length === 0 ? emptyPlaceholder : ''}
              autoComplete="off"
              type="email"
            />
          ) : (
            <Input
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={emails.length === 0 ? emptyPlaceholder : ''}
              error={!!error}
              autoComplete="off"
              noAutoSelect
              inputProps={{ type: 'email' }}
              fullWidth
              sx={{
                '&& .MuiInputBase-root': { border: 'none', px: 0 },
                '&& .MuiInputBase-root.Mui-error': { backgroundColor: 'transparent' },
              }}
            />
          )}
        </Flex>
        {isShowRoleSelection && (
          <FlexCenVer sx={{ flexShrink: 0, height: 32 }}>
            <RoleSelect currentRole={currentRole} updateMemberRole={(newRole) => onSetRole?.(newRole)} />
          </FlexCenVer>
        )}
      </StyledPaper>
      {error && (
        <Typography variant="body-xs-rg" color={token.textDanger}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default EmailChipsInput;
